import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FlightSearchActions,
  getActiveFiltersCount,
  getHasSetFareClassFilter,
  hasSetNonFareclassFiltersSelector,
} from "@b2bportal/core-flights";
import { useFlightStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreFlightsComponent,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import { CalendarPicker, useFlightShopSearch } from "@tenant/flights";
import { Button, ButtonTypeEnum } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./FlightListNoFlights.module.scss";
import LoadingImage from "@/assets/images/loading-image.svg?react";

export type FlightListNoFlightsComponentProps = PlatformComponentProps;
export const FlightListNoFlights = ({
  className,
}: FlightListNoFlightsComponentProps) => {
  const COMPONENT_KEY = CoreFlightsComponent.FlightListNoFlights;
  const styles = useFlightStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { t } = useI18nContext();
  const dispatch = useDispatch();
  const numberOfFiltersApplied = useSelector(getActiveFiltersCount);

  const {
    context: { openCalendar },
    handlers: { onDateSelectionClick: changeDates, onClose, handleOnSearch },
  } = useFlightShopSearch();

  const hasAppliedFareClassFilter = useSelector(getHasSetFareClassFilter);
  const hasAppliedNonFareclassFilter = useSelector(
    hasSetNonFareclassFiltersSelector
  );

  const noFlightsSubtitle = useMemo(() => {
    return hasAppliedFareClassFilter
      ? t("fareClassFlightsNotFound")
      : hasAppliedNonFareclassFilter
        ? t("core-flights.shopPage.noFlightsSubtitle")
        : t("noFlightsAvailable");
  }, [hasAppliedFareClassFilter, hasAppliedNonFareclassFilter, t]);

  return (
    <div className={clsx(block, className)}>
      <LoadingImage aria-hidden="true" />
      <h2 className={cn("title")}>
        {t("core-flights.shopPage.noFlightsAvailable")}
      </h2>
      <p className={cn("subtitle")}>{noFlightsSubtitle}</p>
      <div className={cn("actions")}>
        <Button
          className={cn("change-dates-button")}
          buttonType={ButtonTypeEnum.Primary}
          onClick={changeDates}
        >
          {t("changeDates")}
        </Button>

        {numberOfFiltersApplied > 0 && (
          <Button
            className={cn("reset-filters-button")}
            buttonType={ButtonTypeEnum.Primary}
            onClick={() => dispatch(FlightSearchActions.resetFilters())}
          >
            {t("searchFilter.reset")}
          </Button>
        )}
      </div>

      {openCalendar && (
        <CalendarPicker
          open={openCalendar}
          onClose={onClose}
          onSearch={handleOnSearch}
        />
      )}
    </div>
  );
};
